<script setup lang="ts">
import FilterSearch from './fields/FilterSearch.vue'
import Container from '~/components/layout/Container.vue'
import type {
  EstateListingEssentialResponse,
  EstateListingResponse,
} from '~/types/api/EstateListing'
import type { useFilterState } from '~/composables/filter/useFilterState'
import Icon from '~/components/global/Icon.vue'
import type { EstateType } from '~/types/EstateType'

const props = defineProps<{
  officesResponse: EstateListingResponse | EstateListingEssentialResponse | null
  officesPending: boolean
  estateType: EstateType
  filterState: ReturnType<typeof useFilterState>
  isOnMap?: boolean
}>()

/**
 * Filter expansion state
 */
const isFilterExpanded = ref(false)

function toggleBodyScroll() {
  if (isFilterExpanded.value) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
}

function toggleFilterExpanded() {
  isFilterExpanded.value = !isFilterExpanded.value
  toggleBodyScroll()
}

function hideFilterExpanded() {
  isFilterExpanded.value = false
  toggleBodyScroll()
}

function resetFilter(withShowResults: boolean) {
  props.filterState.actions.resetFilter()

  if (withShowResults) {
    showResults()
  }
}

async function showResults() {
  hideFilterExpanded()

  const focusedElement = document.querySelector(':focus') as HTMLElement | null
  if (focusedElement) {
    focusedElement.blur()
  }

  await new Promise((resolve) => setTimeout(resolve, 100))

  const listingScrollHookEl = document.querySelector(
    '#listing-scroll-hook'
  ) as HTMLElement | null

  if (!listingScrollHookEl) return

  scrollToElement({
    element: listingScrollHookEl,
    ignoreHeaderHeight: true,
  })
}

const skeletonCount = (() => {
  const skeletonCounts = {
    office: 6,
    retail: 4,
    warehouse: 5,
  }

  return skeletonCounts[props.estateType] || 5
})()

const isFiltered = computed(() => {
  const filterKeys = { ...(props.filterState.filterValues.value || {}) }

  if (props.isOnMap) {
    delete filterKeys.latitude_from
    delete filterKeys.latitude_to
    delete filterKeys.longitude_from
    delete filterKeys.longitude_to
  }

  return Object.keys(filterKeys).length > 0
})
</script>

<template>
  <div :class="['c-estate-listing-filter', isOnMap && 'is-on-map']">
    <Container class="c-estate-listing-filter__container">
      <form
        action=""
        :class="['c-estate-listing-filter__main', isOnMap && 'is-on-map']"
        @submit.prevent="showResults"
      >
        <div
          v-if="!officesResponse?.data && officesPending"
          class="c-estate-listing-filter__skeleton"
        >
          <div
            v-for="i in skeletonCount"
            :key="`skeleton-${i}`"
            class="sc-skeleton"
          />
        </div>

        <template v-else-if="officesResponse?.count !== undefined">
          <FilterSearch
            class="c-estate-listing-filter__search"
            :offices-pending="officesPending"
            :filter-state="filterState"
            :estate-type="estateType"
            :show-results="showResults"
          />

          <IButton
            class="c-estate-listing-filter__trigger has-icon"
            color="primary"
            @click="toggleFilterExpanded"
          >
            <Icon name="filter" />

            {{ $t('filter.trigger') }}
          </IButton>

          <div
            :class="[
              'c-estate-listing-filter__other',
              isFilterExpanded && 'is-expanded',
            ]"
          >
            <div class="c-estate-listing-filter__other-head">
              <div class="sc-title">
                {{ $t('filter.title') }}
              </div>

              <IButton
                class="sc-close has-icon"
                color="danger"
                outline
                @click="toggleFilterExpanded"
              >
                <Icon name="close-thick" />
              </IButton>
            </div>

            <div class="c-estate-listing-filter__other-inner">
              <slot />

              <IButton
                color="danger"
                :disabled="!isFiltered"
                class="sc-reset"
                size="sm"
                :title="$t('filter.reset')"
                @click="resetFilter(false)"
              >
                <Icon name="close-thick"
              /></IButton>
            </div>

            <div class="c-estate-listing-filter__other-actions">
              <IButton
                color="danger"
                :disabled="!isFiltered"
                @click="resetFilter(true)"
              >
                {{ $t('filter.reset') }}</IButton
              >

              <IButton
                color="secondary"
                :loading="officesPending"
                :show-loading-icon="true"
                @click="showResults"
              >
                {{
                  $t(
                    'filter.showResults',
                    {
                      _0: officesResponse.count,
                    },
                    officesResponse.count
                  )
                }}
              </IButton>
            </div>
          </div>
        </template>
      </form>
    </Container>
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;
@use '@/assets/styles/mixins/skeleton' as skeleton;

.c-estate-listing-filter {
  &.is-on-map {
    background-color: var(--color-green-primary-light-2);
  }

  &__container {
    @include breakpoint-down('lg') {
      padding: 0;
    }
  }

  &__main {
    background-color: var(--color-green-primary-light-2);
    padding: 14px 20px;
    border-radius: 6px;
    display: flex;
    gap: 8px;

    &.is-on-map {
      padding-left: 0;
      padding-right: 0;
    }

    @include breakpoint-down('lg') {
      border-radius: 0;

      &.is-on-map {
        padding-left: 20px;
        padding-right: 20px;
      }
    }
  }

  &__skeleton {
    display: flex;
    gap: 8px;
    width: 100%;

    .sc-skeleton {
      @include skeleton.skeleton;

      --skeleton-background-color: var(--color-light);

      flex: 0 0 auto;
      height: 52px;
      width: 160px;

      &:first-child {
        width: auto;
        flex: 1 1 auto;
      }

      &:last-child {
        width: 52px;
      }

      @include breakpoint-down('xl') {
        width: 140px;
      }

      @include breakpoint-down('md') {
        display: none;
        width: 95px;

        &:nth-child(1),
        &:nth-child(2) {
          display: block;
        }
      }
    }
  }

  &__search {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__trigger {
    --button--icon--font-size: 16px;
    --button--font-size: 15px;
    --button--padding-left: 16px;
    --button--padding-right: 16px;

    @include breakpoint-up('lg') {
      display: none;
    }
  }

  &__other {
    flex: 0 0 auto;
    min-width: 0;

    @include breakpoint-down('md') {
      display: none;
      flex-direction: column;
      padding: var(--container-padding);
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 9999;
      background-color: hsla(
        var(--color-primary-h),
        var(--color-primary-s),
        var(--color-primary-l),
        0.7
      );
      backdrop-filter: blur(10px);

      &.is-expanded {
        display: flex;
        overflow: hidden auto;
      }
    }
  }

  &__other-inner {
    display: flex;
    gap: 8px;
    position: relative;

    .sc-reset {
      width: 52px;
      transition: all var(--transition-duration)
        var(--transition-timing-function);

      &:disabled,
      &.-disabled {
        --button--background: var(--color-light);
        --button--color: var(--color-gray-500);
        opacity: 0.5;
      }

      @include breakpoint-down('md') {
        display: none;
      }
    }

    @include breakpoint-down('md') {
      background-color: var(--color-light);
      flex-direction: column;
      gap: 0;
      flex: 1 1 auto;
      padding: var(--container-padding);
    }
  }

  &__other-head {
    @include breakpoint-up('lg') {
      display: none;
    }

    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid var(--color-border);
    background-color: var(--color-light);
    padding: 12px 12px 12px 20px;

    .sc-title {
      font-size: 24px;
      font-weight: 600;
    }

    :deep(.button) {
      --button--icon--font-size: 12px;
      --button--padding-left: 12px;
      --button--padding-right: 12px;
      --button--border-width: 0;
      --button--min-height: 34px;
    }
  }

  &__other-actions {
    @include breakpoint-up('lg') {
      display: none;
    }

    display: flex;
    flex-direction: column;
    gap: 8px;
    border-top: 1px solid var(--color-border);
    padding: 12px 20px;
    background-color: var(--color-light);

    :deep(.button.-danger) {
      &:disabled,
      &.-disabled {
        --button--background: var(--color-gray-300);
      }
    }
  }
}
</style>
